<template>
  <d-form>
    <d-form-row class="form-group mb-2">
      <d-col cols="12" md="6">
        <div class="d-flex justify-content-between w-100 p-[5px]">
          <label class="d-flex align-items-end mb-0">
            {{ $t('wordSeparator.form.text.label') }}
          </label>
          <d-col class="form-group d-flex flex-column mb-0 p-0 align-items-end">
            <label class="mb-0" for="feInputNewsletter">
              {{ $t('wordSeparator.form.variations.label') }}
            </label>
            <d-checkbox
              toggle
              :checked="this.variations"
              class="custom-toggle-sm mb-0"
              style="margin-right: 12px"
              @change="toggleVariations"
            >
              {{ $t('wordSeparator.form.variations.option') }}
            </d-checkbox>
          </d-col>
        </div>
      </d-col>
      <d-col cols="12" md="6" class="d-none d-md-flex flex-column">
        <copy-text-title
          :onCopyText="copyText"
          :title="$t('wordSeparator.form.result.label')"
        />
      </d-col>
    </d-form-row>
    <d-form-row class="form-group mb-1">
      <d-col cols="12" md="6">
        <editor
          :model.sync="model.text"
          ref="editor"
          reference="text"
          @ready="setQuill"
          :editorOption="{
            modules: {
              toolbar: null,
              keyboard: {
                bindings: {
                  bold: {
                    key: 'B',
                    shortKey: true,
                    handler: selectWord,
                  },
                },
              },
            },
            placeholder: $t('wordSeparator.form.text.placeholder'),
          }"
        />
      </d-col>
      <d-col cols="12" md="6" class="d-flex d-md-none flex-column my-2">
        <copy-text-title
          :onCopyText="copyText"
          :title="$t('wordSeparator.form.result.label')"
        />
      </d-col>
      <d-col cols="12" md="6">
        <editor
          :model.sync="model.result"
          reference="result"
          :editorOption="{
            modules: { toolbar: null },
            placeholder: $t('wordSeparator.form.result.placeholder'),
          }"
        />
      </d-col>
    </d-form-row>
    <d-form-row class="form-group" v-if="variations">
      <d-col cols="12">
        <action-buttons :onGenerate="generateResult" :onClear="cleanText" />
      </d-col>
      <d-col cols="12" class="d-flex justify-content-between md-mt-0 mt-1">
        <word-separator-list-table
          :listMultipleCases="model.listMultipleCases"
        />
      </d-col>
    </d-form-row>

    <d-form-row class="form-group" v-else>
      <d-col cols="12" md="6" class="pt-1">
        <action-buttons :onGenerate="generateResult" :onClear="cleanText" />
      </d-col>
      <d-col
        cols="12"
        md="6"
        class="d-flex justify-content-between md-mt-0 pt-0"
      >
        <label class="w-75">
          {{ $t('wordSeparator.form.repeatedWords.label') }}
          {{ model.repeatedWords.join(', ') }}
        </label>
        <label>
          {{ $t('wordSeparator.form.totalWords.label') }}
          {{ model.list.length }}
        </label>
      </d-col>
    </d-form-row>
  </d-form>
</template>

<script>
import { htmlToText } from 'html-to-text';

import Editor from '_shared/Editor.vue';
import CopyTextTitle from '_shared/CopyTextTitle.vue';
import ActionButtons from '_shared/ActionButtons.vue';
import WordSeparatorListTable from './WordSeparatorListTable.vue';

export default {
  name: 'word-separator-form',
  components: {
    Editor,
    WordSeparatorListTable,
    CopyTextTitle,
    ActionButtons,
  },
  data() {
    return {
      timer: null,
      quill: null,
      model: {
        text: '',
        list: [],
        listMultipleCases: [],
        repeatedWords: [],
        result: '',
      },
      variations: false,
    };
  },
  methods: {
    cleanText() {
      this.model.text = '';
      this.cleanResult();
    },
    cleanResult() {
      this.model.repeatedWords = [];
      this.model.listMultipleCases = [];
      this.model.list = [];
      this.model.result = '';
    },
    setQuill(quill) {
      this.quill = quill;
    },
    selectWord(range) {
      const word =
        (document.selection && document.selection.createRange().text) ||
        (window.getSelection && window.getSelection().toString());
      const formatted = word.toUpperCase();

      if (this.model.list.includes(formatted)) {
        // eslint-disable-next-line
        alert(this.$t('wordSeparator.alert.repeatedWord'));
      } else {
        this.model.list.push(formatted);

        const { index, length } = range;
        const options = [];
        if (index > 0) {
          options.push({ retain: index });
        }
        options.push({ delete: length });
        options.push({ insert: formatted });
        this.quill.updateContents({
          ops: options,
        });
        this.quill.formatText(index, length, 'bold', true);
      }
    },
    generateResult() {
      if (this.model.result !== '') this.cleanResult();
      if (this.variations) {
        const cleanedHtml = this.model.text.replace(/(<br>)+/g, '<p><br></p>');
        const parts = cleanedHtml.split('<p><br></p>');

        parts.forEach(part => {
          const listWordsPart = this.extractUppercaseTextFromHTML(part);
          if (listWordsPart.length) {
            this.model.listMultipleCases.push({
              repeatedWords: this.model.repeatedWords,
              totalWord: listWordsPart.length,
            });
          }

          this.model.result += part;
          this.model.result += '<p><br></p>';
          this.model.result += listWordsPart.join('<br>');
          this.model.result += '<p><br></p><p><br></p><p><br></p>';
        });
      } else {
        this.model.list = this.extractUppercaseTextFromHTML(this.model.text);
        this.model.result += this.model.list.join('<br>');
        this.model.result += '<p><br></p><p><br></p><p><br></p>';
      }
    },
    convertHtmlToText(html) {
      return htmlToText(html, {
        wordwrap: false,
        selectors: [
          { selector: 'br', format: 'inlineBreak' },
          {
            selector: 'p',
            options: { leadingLineBreaks: 1, trailingLineBreaks: 1 },
          },
        ],
        preserveNewlines: true,
      });
    },
    copyText() {
      const plainText = this.convertHtmlToText(this.model.result);
      const plainTextFormatted = plainText.replace(/\n+/g, match => {
        const count = match.length;
        if (count === 2) return '\n';
        if (count === 5) return '\n\n';
        if (count > 5) return '\n\n\n';
        return match;
      });

      navigator.clipboard.writeText(plainTextFormatted).catch(err => {
        console.error('Erro ao copiar texto:', err);
      });
    },
    removeShortAndRoman(list) {
      const romanNumerals =
        /^M{0,3}(?:C[MD]|D?C{0,3})(?:X[CL]|L?X{0,3})(?:I[XV]|V?I{0,3})$/i;
      return list.filter(word => word.length >= 3 && !romanNumerals.test(word));
    },
    extractUppercaseTextFromHTML(html) {
      const textOnly = html.replace(/<[^>]*>/g, ' ');
      const textWithoutQuotes = textOnly.replace(/['"`´‘’“”]/g, '');
      const uppercaseMatches = [
        ...(textWithoutQuotes.match(
          /(?:^|\s)([A-ZÀ-ÝÇÂÊÎÔÛÃÕÄËÏÖÜ]+(?:-[A-ZÀ-ÝÇÂÊÎÔÛÃÕÄËÏÖÜ]+)*)(?=[\s.,;!?]|$)/gu,
        ) || []),
      ].map(word => word.trim());

      this.model.repeatedWords = this.getRepeatedWords(uppercaseMatches);
      const list = [...new Set(uppercaseMatches)];

      const result = this.removeShortAndRoman(list);

      return result;
    },
    getRepeatedWords(array) {
      const repeatedWords = [
        ...new Set(
          array.filter((item, index, arr) => arr.indexOf(item) !== index),
        ),
      ];

      return this.removeShortAndRoman(repeatedWords);
    },
    toggleVariations() {
      this.variations = !this.variations;
      if (this.model.text !== '') this.generateResult();
    },
  },
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
  &:hover {
    color: red;
  }
}
.text-area {
  height: 200px;
}
</style>
